import React, { useState } from "react"
import { Button } from "react-bootstrap"
import Form from "react-bootstrap/Form"
import VesselTrackingCreateListModal from "./VesselTrackingCreateListModal"
import VesselTrackingAddVesselModal from "./VesselTrackingAddVesselModal"
import { VesselTrackingList } from "../hooks/useFetchList"
import { VesselInfo } from "../hooks/useFetchVesselInfo"
import { TrackVesselStruct } from "../hooks/useTrackVessel"
import { getBGPill, formatReceivedAgo } from "../helpers/helper"
import { Tooltip, OverlayTrigger } from "react-bootstrap"
import { toast } from "react-toastify"
import { Modal } from "react-bootstrap"
import { useDeleteVesselFromList } from "../hooks/useDeleteVesselFromList"
import { isAxiosError } from "axios"

export interface VesselTrackingInfoProps {
  trackingList: VesselTrackingList[]
  activeList: VesselTrackingList
  fetchVesselTrackingList: () => Promise<void>
  appendVesselTrackingList: (list: VesselTrackingList) => void
  setActiveList: React.Dispatch<
    React.SetStateAction<VesselTrackingList | undefined>
  >
  vesselInfos: VesselInfo[] | undefined
  fetchVesselInfo: (listID: string[]) => Promise<void>
  isGetVesselInfoLoading: boolean
  trackVesselDetails: TrackVesselStruct[] | undefined
  trackVessel: (imos: string[]) => Promise<void>
  isTrackVesselLoading: boolean
  clearTrackVesselDetails: () => void
  onSelectVessel: (lat: number, lon: number) => void
  statusFilter: string
  loadingFilter: string
  onStatusFilterChange: (value: string) => void
  onLoadingFilterChange: (value: string) => void
}

const SELECTED_BG = "#cfe2ff"
const DEFAULT_BG = "white"

function VesselTrackingInfo({
  trackingList,
  activeList,
  setActiveList,
  fetchVesselTrackingList,
  appendVesselTrackingList,
  vesselInfos,
  fetchVesselInfo,
  isGetVesselInfoLoading,
  trackVesselDetails,
  trackVessel,
  isTrackVesselLoading,
  clearTrackVesselDetails,
  onSelectVessel,
  statusFilter,
  loadingFilter,
  onStatusFilterChange,
  onLoadingFilterChange,
}: VesselTrackingInfoProps) {
  const [showListModal, setShowListModal] = useState<boolean>(false)
  const [showAddVesselModal, setAddVesselModal] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>("")
  const prevetList =
    trackingList.filter((list) => list.listType === "prevet") || null
  const userList = trackingList.filter((list) => list.listType === "user" || null)
  const [selectedListId, setSelectedListId] = useState<string>(
    activeList?.id || ""
  )
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [selectedImos, setSelectedImos] = useState<Set<string>>(new Set())
  const { deleteVesselFromList, isDeleteVesselLoading } =
    useDeleteVesselFromList()

  const toggleVesselSelection = (imo: string) => {
    setSelectedImos((prev) => {
      const next = new Set(prev)
      if (next.has(imo)) next.delete(imo)
      else next.add(imo)
      return next
    })
  }

  const handleCloseListModal = () => {
    setShowListModal(false)
  }
  const handleCloseAddVesselModal = () => {
    setAddVesselModal(false)
  }

  const handleListCreated = (newList: VesselTrackingList) => {
    appendVesselTrackingList(newList)
    setActiveList(newList)
    setSelectedListId(newList.id)
    setShowListModal(false)
    setAddVesselModal(true)
  }

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    onStatusFilterChange(e.target.value)
    setSelectedImos(new Set())
  }

  const handleLoadingFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onLoadingFilterChange(e.target.value)
    setSelectedImos(new Set())
  }

  const handleDeleteSelected = async () => {
    try {
      const idsToDelete = [...selectedImos]
        .map((imo) => vesselInfos?.find((v) => v.vesselImo === imo)?.id)
        .filter((id): id is string => !!id)
      for (const id of idsToDelete) {
        await deleteVesselFromList(id)
      }
      toast.success(`${idsToDelete.length} vessel(s) removed from favourites`, {
        position: "bottom-right",
        autoClose: 8000,
      })
      setSelectedImos(new Set())
    } catch (err: unknown) {
      const message = "Failed to delete vessel(s) from list"
      console.error(message, err)
      if (isAxiosError(err)) {
        toast.error(message, { position: "bottom-right", autoClose: 8000 })
      }
    } finally {
      await fetchVesselInfo([selectedListId])
      setShowDeleteModal(false)
    }
  }

  const tooltip = (
    <Tooltip id="warning-tooltip">No records found for this vessel</Tooltip>
  )

  return (
    <>
      {/* Modal for create list */}
      <VesselTrackingCreateListModal
        showListModal={showListModal}
        closeListModal={handleCloseListModal}
        vesselList={trackingList}
        fetchVesselTrackingList={fetchVesselTrackingList}
        onListCreated={handleListCreated}
        activeList = {activeList}
        setActiveList = {setActiveList}
      />
      <VesselTrackingAddVesselModal
        selectedListId={activeList.id}
        showAddVesselModal={showAddVesselModal}
        closeAddVesselModal={handleCloseAddVesselModal}
        fetchVesselInfo={fetchVesselInfo}
        trackingList={trackingList}
      />
      <div className="VesselTrackingContainer_info bg-white pt-2 ps-2">
        <div className="controls d-flex flex-nowrap gap-2 align-items-end mb-3">
          <Button
            variant="primary"
            className="text-nowrap"
            onClick={() => setShowListModal(true)}
          >
            Create New List
          </Button>
          <Button
            variant="primary"
            className="text-nowrap"
            onClick={() => setAddVesselModal(true)}
            disabled={selectedListId.includes("select_all")}
          >
            Edit Vessel List
          </Button>
          {/* Vessel List select */}
          <div style={{ minWidth: 0 }}>
            <div className="VesselInfoSelectTitle">Vessel List</div>
            <Form.Select
              className="pe-3"
              value={selectedListId}
              onChange={(e) => {
                const selectedId = e.target.value
                setSelectedImos(new Set())
                setSearchQuery("")
                if (selectedId === "select_all_prevet" && prevetList) {
                  fetchVesselInfo(prevetList?.map((list) => list.id))
                  setSelectedListId("select_all_prevet")
                } else if (selectedId === "select_all_user" && userList) {
                  fetchVesselInfo(userList.map((list) => list.id))
                  setSelectedListId("select_all_user")
                } else {
                  setSelectedListId(selectedId)
                  const foundList = trackingList.find(
                    (list) => list.id === selectedId
                  )
                  if (foundList) {
                    setActiveList(foundList)
                  }
                }
              }}
            >
              <optgroup label="Prevet">
                {prevetList && prevetList.length > 0 ? (
                  <>
                    <option key={"select_all_prevet"} value="select_all_prevet">
                      Select All
                    </option>
                    {prevetList.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.listName}
                      </option>
                    ))}
                  </>
                ) : (
                  <option disabled>No Prevet List Available</option>
                )}
              </optgroup>
              <optgroup label="User's List">
                {userList && userList.length > 0 ? (
                  <>
                    <option key={"select_all_user"} value="select_all_user">
                      Select All
                    </option>
                    {userList.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.listName}
                      </option>
                    ))}
                  </>
                ) : (
                  <option disabled>No User List Available</option>
                )}
              </optgroup>
            </Form.Select>
          </div>
          {/* Ballast/Laden select */}
          <div style={{ minWidth: 0 }}>
            <div className="VesselInfoSelectTitle">B/L</div>
            <Form.Select
              className="pe-6"
              value={loadingFilter}
              onChange={handleLoadingFilter}
            >
              <option value="all">All</option>
              <option value="Ballast">Ballast</option>
              <option value="Laden">Laden</option>
            </Form.Select>
          </div>
          {/* Status select */}
          <div style={{ minWidth: 0 }}>
            <div className="VesselInfoSelectTitle">Status</div>
            <Form.Select
              className="pe-6"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All</option>
              <option value="UWAY">Underway</option>
              <option value="MOOR">Moored</option>
              <option value="ANCH">Anchorage</option>
            </Form.Select>
          </div>
          {/* Delete button — visible only when vessels are selected */}
          {selectedImos.size > 0 && (
            <div
              className="ms-auto align-self-end pe-6"
              style={{ flexShrink: 0 }}
            >
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={selectedListId.includes("all")}
              >
                Delete ({selectedImos.size})
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div
          className="mt-2 pe-2 tablePane table-responsive"
          style={
            {
              "--search-bar-height":
                trackVesselDetails && trackVesselDetails.length > 100
                  ? "42px"
                  : "0px",
            } as React.CSSProperties
          }
        >
          {trackVesselDetails && trackVesselDetails.length > 100 && (
            <div
              className="py-2"
              style={{
                textAlign: "right",
                position: "sticky",
                top: 0,
                background: "white",
                zIndex: 2,
              }}
            >
              <label
                htmlFor="vessel-search"
                className="mb-0 text-muted me-2"
                style={{
                  fontSize: 14,
                  whiteSpace: "nowrap",
                  verticalAlign: "middle",
                }}
              >
                Search Vessel in list:
              </label>
              <input
                id="vessel-search"
                type="text"
                className="form-control form-control-sm d-inline-block"
                style={{ width: 220, verticalAlign: "middle" }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    background: "white",
                    width: 36,
                    minWidth: 36,
                  }}
                />
                <th
                  style={{
                    position: "sticky",
                    left: 36,
                    zIndex: 2,
                    background: "white",
                    fontWeight: "bold",
                  }}
                >
                  Name
                </th>
                <th style={{ fontWeight: "bold" }}>B/L</th>
                <th style={{ fontWeight: "bold" }}>Sta</th>
                <th style={{ fontWeight: "bold" }}>Live Speed</th>
                <th style={{ fontWeight: "bold" }}>24h avg</th>
                <th style={{ fontWeight: "bold" }}>48h avg</th>
                <th style={{ fontWeight: "bold" }}>72h avg</th>
                <th style={{ fontWeight: "bold" }}>6M avg</th>
                <th style={{ fontWeight: "bold" }}>Latest AIS</th>
              </tr>
            </thead>
            <tbody>
              {isTrackVesselLoading || isGetVesselInfoLoading ? (
                <tr>
                  <td colSpan={10} className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : vesselInfos && vesselInfos.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-2">
                    No vessels found in this list.
                  </td>
                </tr>
              ) : trackVesselDetails && trackVesselDetails.length !== 0 ? (
                trackVesselDetails
                  .filter((vessel) => {
                    const matchesStatus =
                      statusFilter === "all" || vessel.status === statusFilter
                    const matchesBL =
                      loadingFilter === "all" ||
                      vessel.loading === loadingFilter
                    const q = searchQuery.trim().toLowerCase()
                    const matchesSearch =
                      q === "" ||
                      vessel.name.toLowerCase().includes(q) ||
                      vessel.imo.toString().toLowerCase().includes(q)
                    return matchesStatus && matchesBL && matchesSearch
                  })
                  .sort((a, b) => {
                    return a.name.localeCompare(b.name)
                  })
                  .map((vessel) => {
                    const isSelected = selectedImos.has(vessel.imo)
                    const stickyBg = isSelected ? SELECTED_BG : DEFAULT_BG
                    return (
                      <tr
                        key={vessel.imo}
                        className={isSelected ? "table-primary" : ""}
                      >
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            background: stickyBg,
                            width: 36,
                            minWidth: 36,
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVesselSelection(vessel.imo)}
                          />
                        </td>
                        <td
                          onClick={() => {
                            if (!vessel.vesselPosition?.length) {
                              toast.warning("Vessel has no records", {
                                position: "bottom-right",
                                autoClose: 8000,
                              })
                              return
                            }
                            const latest = [...vessel.vesselPosition].sort(
                              (a, b) =>
                                new Date(b.pos_date).getTime() -
                                new Date(a.pos_date).getTime()
                            )[0]
                            onSelectVessel(latest.lat, latest.lon)
                          }}
                          style={{
                            cursor: "pointer",
                            position: "sticky",
                            left: 36,
                            zIndex: 1,
                            background: stickyBg,
                          }}
                        >
                          <p>
                            {vessel.name}
                            {vessel.vesselPosition === null && (
                              <OverlayTrigger placement="top" overlay={tooltip}>
                                <span className="badge bg-warning ms-1 text-black">
                                  <strong>!</strong>
                                </span>
                              </OverlayTrigger>
                            )}
                          </p>
                        </td>
                        <td>{vessel.loading ? vessel.loading : "n/a"}</td>
                        <td>{vessel.status ? vessel.status : "n/a"}</td>
                        <td>
                          {/* {(() => {
                            const sog = vessel.vesselPosition?.[0]?.sog
                            const isNumber =
                              typeof sog === "number" && !Number.isNaN(sog)

                            return (
                              <span
                                className={`badge bg-${getBGPill(
                                  isNumber ? sog : null
                                )} px-2 py-1 rounded-pill text-white fs-6`}
                              >
                                {isNumber ? sog.toFixed(2) : "—"}
                              </span>
                            )
                          })()} */}
                          <span
                            className={`badge bg-${getBGPill(
                              vessel.live_speed
                            )} px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {vessel.live_speed?.toFixed(2) || "n/a"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${getBGPill(
                              vessel.h24
                            )} px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {vessel.h24?.toFixed(2) || "n/a"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${getBGPill(
                              vessel.h48
                            )} px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {vessel.h48?.toFixed(2) || "n/a"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${getBGPill(
                              vessel.h72
                            )} px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {vessel.h72?.toFixed(2) || "n/a"}
                          </span>
                        </td>
                        {/* {(() => {
                          const sixMonthsAgo = new Date()
                          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
                          const recentPositions = (
                            vessel.vesselPosition ?? []
                          ).filter(
                            (pos) => new Date(pos.pos_date) >= sixMonthsAgo
                          )
                          const avg6m =
                            recentPositions.length > 0
                              ? recentPositions.reduce(
                                  (sum, pos) => sum + pos.sog,
                                  0
                                ) / recentPositions.length
                              : null

                          return (
                            <td>
                              <span
                                className={`badge bg-${getBGPill(
                                  avg6m
                                )} px-2 py-1 rounded-pill text-white fs-6`}
                              >
                                {avg6m != null && !isNaN(avg6m)
                                  ? avg6m.toFixed(2)
                                  : "n/a"}
                              </span>
                            </td>
                          )
                        })()} */}
                        <td>
                          <span
                            className={`badge bg-${getBGPill(
                              vessel.avg_speed_6m
                            )} px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {vessel.avg_speed_6m?.toFixed(2) || "n/a"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              vessel.received !== null
                                ? "bg-success"
                                : "bg-dark"
                            } px-2 py-1 rounded-pill text-white fs-6`}
                          >
                            {formatReceivedAgo(vessel.received)}
                          </span>
                        </td>
                      </tr>
                    )
                  })
              ) : (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-2">
                    Vessel tracking data not available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Modal for delete */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Delete Vessel{selectedImos.size > 1 ? "s" : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {selectedImos.size} vessel
          {selectedImos.size > 1 ? "s" : ""} from the list  <span className="fw-bold">{activeList.listName}</span>?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleteVesselLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDeleteSelected}
            disabled={isDeleteVesselLoading}
          >
            {isDeleteVesselLoading
              ? "Deleting..."
              : `Delete (${selectedImos.size})`}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default VesselTrackingInfo
